// Lista de promos de referido sobre el panel común: búsqueda y estado, y la tabla en el orden común
// seguida de evento, premios y presupuesto.
import type { ReactNode } from 'react';
import { CellLines, CellText, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    isDimmedLifecycleStatus,
    LifecycleListPanel,
    LifecycleNameCell,
} from '@/shared/patterns/Lifecycle';
import { REFERRAL_PROMOTION_HELP } from '../constants/helpText';
import type { SalePromotionListItem } from '../api/promotionsContract';
import { LIST_FILTER_STATUSES, listStatusOf, type StatusFilter } from '../lib/promotionsList';
import { formatConsumed, formatShortValidity, formatValidity } from '../lib/promotionsRow';
import { triggerShortLabel } from '../lib/referralPromotionOptions';
import { referralRewardText } from '../lib/referralPromotionText';
import { ListStatusBadge } from './ListStatusBadge';
import { ALL_RETAILERS } from '../constants/retailerFilter';

const TABLE_MIN_WIDTH = '560px';
/** Archivadas no se listan aquí: el back no las trae sin `status`. */
const REFERRAL_FILTER_STATUSES = LIST_FILTER_STATUSES.filter((status) => status !== 'ARCHIVED');

export interface ReferralListFilters {
    search: string;
    status: StatusFilter;
    /** `ALL_RETAILERS` o la cadena; solo se usa cuando la lista junta varias. */
    retailerId: string;
}

export const EMPTY_REFERRAL_FILTERS: ReferralListFilters = {
    search: '',
    status: 'ALL',
    retailerId: ALL_RETAILERS,
};

export interface ReferralPromotionsListProps {
    items: SalePromotionListItem[];
    loaded: boolean;
    now: Date;
    filters: ReferralListFilters;
    onFiltersChange: (filters: ReferralListFilters) => void;
    selectedId: string | null;
    onSelect: (promo: SalePromotionListItem) => void;
    canCreate: boolean;
    onCreate: () => void;
    emptyText: string;
    footer?: ReactNode;
    /** Nombre por cadena cuando la lista junta varias; con dos o más hay columna y filtro. */
    retailerNames?: ReadonlyMap<string, string>;
}

export function ReferralPromotionsList({
    items,
    loaded,
    now,
    filters,
    onFiltersChange,
    selectedId,
    onSelect,
    canCreate,
    onCreate,
    emptyText,
    footer,
    retailerNames,
}: ReferralPromotionsListProps) {
    const byRetailer = retailerNames !== undefined && retailerNames.size > 1;
    const columns: DataTableColumn<SalePromotionListItem>[] = [
        {
            key: 'name',
            label: 'Promo de referido',
            cell: (promo) => (
                <LifecycleNameCell
                    name={promo.name}
                    dimmed={isDimmedLifecycleStatus(listStatusOf(promo, now))}
                />
            ),
        },
        {
            key: 'status',
            label: 'Estado',
            width: '84px',
            cellOverflow: 'nowrap',
            cell: (promo) => <ListStatusBadge promo={promo} now={now} />,
        },
        {
            key: 'validity',
            label: 'Vigencia',
            width: '108px',
            cellTitle: (promo) => formatValidity(promo),
            cell: (promo) => (
                <span className="text-[10.5px] leading-[1.35]">
                    {formatShortValidity(promo, now)}
                </span>
            ),
        },
        ...(byRetailer
            ? [
                  {
                      key: 'retailer',
                      label: 'Cadena',
                      width: '104px',
                      cell: (promo: SalePromotionListItem) => (
                          <span className="text-[10.5px] leading-[1.35]">
                              {retailerNames.get(promo.retailerId) ?? promo.retailerId}
                          </span>
                      ),
                  },
              ]
            : []),
        {
            key: 'trigger',
            label: 'Evento',
            width: '112px',
            cell: (promo) => (
                <span className="text-[10.5px] leading-[1.35]">
                    {promo.referral ? triggerShortLabel(promo.referral) : '—'}
                </span>
            ),
        },
        {
            key: 'rewards',
            label: 'Premios',
            width: '170px',
            cell: (promo) =>
                promo.referral ? (
                    <CellLines
                        primary={referralRewardText(promo.referral.referrerReward)}
                        secondary={
                            promo.referral.referredReward
                                ? `Recomendado: ${referralRewardText(promo.referral.referredReward)}`
                                : 'El recomendado no gana'
                        }
                    />
                ) : (
                    <CellText>—</CellText>
                ),
        },
        {
            key: 'budget',
            label: 'Presupuesto',
            width: '96px',
            numeric: true,
            note: 'Lo que ya se pagó en premios, contra su tope si lo tiene',
            cell: (promo) => {
                const { consumed, cap } = formatConsumed(promo);
                return cap === null ? (
                    <CellText>{consumed}</CellText>
                ) : (
                    <CellLines primary={consumed} secondary={cap} />
                );
            },
        },
    ];

    return (
        <LifecycleListPanel
            search={{
                label: 'Buscar promo de referido',
                value: filters.search,
                onChange: (search) => {
                    onFiltersChange({ ...filters, search });
                },
            }}
            status={{
                value: filters.status,
                anyValue: 'ALL',
                options: REFERRAL_FILTER_STATUSES.map((status) => ({ value: status, status })),
                onChange: (status) => {
                    onFiltersChange({ ...filters, status: status as StatusFilter });
                },
            }}
            filters={
                byRetailer
                    ? [
                          {
                              label: 'Cadena',
                              help: REFERRAL_PROMOTION_HELP.filterRetailer,
                              value: filters.retailerId,
                              options: [
                                  { value: ALL_RETAILERS, label: 'Todas las cadenas' },
                                  ...[...retailerNames.entries()]
                                      .sort(([, a], [, b]) => a.localeCompare(b, 'es-MX'))
                                      .map(([value, label]) => ({ value, label })),
                              ],
                              onChange: (retailerId) => {
                                  onFiltersChange({ ...filters, retailerId });
                              },
                          },
                      ]
                    : []
            }
            count={{ value: items.length, one: 'promo', many: 'promos' }}
            create={canCreate ? { label: 'Crear promoción', onClick: onCreate } : null}
            footer={
                <>
                    <p className="flex-none border-t border-border px-3 py-1.5 text-[10px] text-muted-foreground">
                        Las promos de referido premian al cliente que recomienda; el recomendado
                        gana solo si la promo lo prende.
                    </p>
                    {footer}
                </>
            }
            table={{
                columns,
                rows: items,
                rowKey: (promo) => promo.promotionId,
                minWidth: TABLE_MIN_WIDTH,
                caption:
                    'Promos de referido con su estado, vigencia, evento, premios y presupuesto.',
                isRowSelected: (promo) => promo.promotionId === selectedId,
                onRowClick: onSelect,
                isLoading: !loaded,
                skeletonRows: 6,
                empty: emptyText,
            }}
        />
    );
}
